import axios from 'axios';
import Cookies from 'js-cookie';
import API, { SOCKET } from './api';
import AppMemoryCookies from '../utils/handleCookies';

export const handleSession = (action, data) => {
  const productId = API.defaults.headers.product.replace(/\./g, '-');

  const activeSessionKey = `app-students-active-session-${productId}`;
  if (action === 'SET') {
    const { user, keepConnected } = data;
    const activeSession = JSON.stringify({
      email: user.email,
      refreshToken: user.refresh_token,
      keepConnected,
    });
    const cookieSettings = {
      expires: keepConnected ? 30 : null,
      samesite: 'None',
      secure: true,
    };
    if (process.env.REACT_APP_WITHOUT_HTTPS) {
      delete cookieSettings.samesite;
      delete cookieSettings.secure;
    }
    Cookies.set(activeSessionKey, activeSession, { ...cookieSettings });
    AppMemoryCookies.setSession(activeSession);
    return 'SET SUCCESS';
  }
  if (action === 'REMOVE') {
    Cookies.remove(activeSessionKey);
    AppMemoryCookies.removeSession();
    return 'REMOVE SUCCESS';
  }
  const storedCookie = Cookies.get(activeSessionKey);
  if (storedCookie) {
    return JSON.parse(storedCookie);
  }
  const memoryCookie = AppMemoryCookies.getSession();
  if (memoryCookie) {
    return JSON.parse(memoryCookie);
  }
  return null;
};

export function setAuthToken(token) {
  if (token) {
    API.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}

export function deleteAuthToken() {
  delete API.defaults.headers.common.Authorization;
}

export const fetchLogin = userData => {
  return API.post('/Auth/login', userData, {
    headers: {
      SocketConnectionId: SOCKET.connectionId,
    },
  })
    .then(res => {
      handleSession('SET', {
        user: res.data,
        keepConnected: userData.keepConnected,
      });
      return res;
    })
    .catch(err => {
      throw err;
    });
};

export const fetchLoginByGuid = guid =>
  API.post(`/Auth/login/guid/${guid}`)
    .then(res => {
      handleSession('SET', {
        user: res.data,
        keepConnected: false,
      });
      return res;
    })
    .catch(err => {
      throw err;
    });

export const fetchRefreshToken = ({ keepConnected }) => {
  const sessionCookie = handleSession('GET');
  if (!sessionCookie) {
    return null;
  }
  const { email, refreshToken } = sessionCookie;
  const hasActiveSession = !!email;
  if (!hasActiveSession && !JSON.parse(keepConnected)) {
    return null;
  }
  const req = {
    email,
    refreshToken,
    remember: true,
    loginProvider: 'webApp',
  };
  return API.post('/Auth/refresh', req, {
    headers: {
      SocketConnectionId: SOCKET.connectionId,
    },
  })
    .then(res => {
      handleSession('SET', {
        user: res.data,
        keepConnected,
      });
      return res;
    })
    .catch(err => {
      throw err;
    });
};

function forceLogout() {
  handleSession('REMOVE');
  localStorage.clear();
  window.location.reload(true);
}

let isAlreadyFetchingAccessToken = false;
let subscribers = [];

function isTokenExpiredError(errorResponse) {
  const status = errorResponse?.status;
  return status === 401;
}

function onAccessTokenFetched(AccessToken) {
  subscribers.forEach(callback => callback(AccessToken));
  subscribers = [];
}

function addSubscriber(callback) {
  subscribers.push(callback);
}

const resetTokenAndReattemptRequest = async error => {
  try {
    const { response: errorResponse } = error;
    const session = handleSession('GET');
    if (!session) {
      forceLogout();
      return Promise.reject(error);
    }

    const retryOriginalRequest = new Promise(resolve => {
      addSubscriber(AccessToken => {
        errorResponse.config.headers.Authorization = `Bearer ${AccessToken}`;
        resolve(axios(errorResponse.config));
      });
    });

    if (!isAlreadyFetchingAccessToken) {
      isAlreadyFetchingAccessToken = true;
      const { email, refreshToken, keepConnected } = session;
      const user = { email, refresh_token: refreshToken };
      const response = await fetchRefreshToken({ user, keepConnected });
      if (!response.data) {
        forceLogout();
        return Promise.reject(error);
      }
      const dataRefreshToken = response.data.refresh_token;
      const dataAccessToken = response.data.access_token;
      if (!dataRefreshToken || !dataAccessToken) {
        forceLogout();
        return Promise.reject(error);
      }
      user.refresh_token = dataRefreshToken;
      setAuthToken(dataAccessToken);
      isAlreadyFetchingAccessToken = false;
      onAccessTokenFetched(dataAccessToken);
    }
    return retryOriginalRequest;
  } catch (err) {
    forceLogout();
    return Promise.reject(err);
  }
};

API.interceptors.response.use(
  response => {
    if (process.env.REACT_APP_DEBUG_CONNECTIONS) {
      console.info('response --> ', response.config.url);
      console.info(response);
    }
    return response;
  },
  error => {
    const errorResponse = error.response;
    if (isTokenExpiredError(errorResponse)) {
      return resetTokenAndReattemptRequest(error);
    }
    return Promise.reject(error);
  },
);

API.interceptors.request.use(request => {
  if (process.env.REACT_APP_DEBUG_CONNECTIONS) {
    console.info('request --> ', request.url);
    console.info(request);
  }
  return request;
});
